import { saveAs } from "file-saver";
import { ACCEPTED_FILE_EXTENSIONS_CASE } from "src/constants";
import {
  CaseListingType,
  ExcelSheetCaseProps,
  ExcelSheetEOBsHeader,
  ExcelSheetEOBsProps,
  ExcelSheetHeader,
} from "src/pages/dashboard/types";
import ExcelJS from "exceljs";
import { EOBsExcelFileType } from "src/pages/EOBsListing/types";
import { format } from "date-fns/format";
import { parse } from "date-fns";

export const downloadZipFile = (base64Data: string, fileName: string) => {
  fetch(`data:applicatiaon/x-zip-compressed;base64,${base64Data}`)
    .then((res) => res.blob())
    .then((blob) => {
      saveAs(blob, fileName);
    });
};

export const downloadPdfFile = (base64Data: string, fileName: string) => {
  fetch(`data:applicatiaon/pdf;base64,${base64Data}`)
    .then((res) => res.blob())
    .then((blob) => {
      saveAs(blob, fileName);
    });
};
export const downloadExcelFile = (base64Data: string, fileName: string) => {
  fetch(
    `data:applicatiaon/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`
  )
    .then((res) => res.blob())
    .then((blob) => {
      saveAs(blob, fileName);
    });
};

export const downloadFileAccordingToExtension = (
  base64Data: string,
  fileName: string
) => {
  const fileExtension = `.${fileName.split(".").pop()}`;
  if (fileExtension === ".xlsx") downloadExcelFile(base64Data, fileName);
  else
    fetch(
      `data:${ACCEPTED_FILE_EXTENSIONS_CASE[fileExtension]};base64,${base64Data}`
    )
      .then((res) => res.blob())
      .then((blob) => {
        saveAs(blob, fileName);
      });
};

type ButtonItem = {
  key: number;
  ins_name: string | null;
  precert?: boolean;
  update?: boolean;
  review?: boolean;
  rx_pa?: boolean;
  update_pa?: boolean;
  review_pa?: boolean;
  [key: string]: any;
};

// type CaseObject = {
//   precert_buttons?: ButtonItem[];
//   update_buttons?: ButtonItem[];
//   review_buttons?: ButtonItem[];
//   rx_pa_buttons?: ButtonItem[];
//   update_pa_buttons?: ButtonItem[];
//   review_pa_buttons?: ButtonItem[];
//   treatment_decision?: boolean;
//   date_of_treatment?: boolean;
// };

const extractButtonLabels = (kase: CaseListingType): string => {
  const labels: string[] = [];
  const mapButtons = (
    buttons: ButtonItem[] | undefined,
    key: keyof ButtonItem,
    label: string
  ) => {
    buttons?.forEach((btn) => {
      if (btn[key]) {
        const insLabel = btn.ins_name ? ` (${btn.ins_name})` : "";
        labels.push(`${label}${insLabel}`);
      }
    });
  };

  if (kase.status_name.toLowerCase() !== "cancelled") {
    if (kase.date_of_treatment && kase.no_action_required) {
      labels.push("Date of Treatment");
    }

    if (kase.treatment_decision) {
      labels.push("Treatment Decision");
    }
    if (kase.copay_claim) {
      labels.push("Copay Claim");
    }
  }else if(kase.no_action_required){
     labels.push("No Action Required");
  }

  mapButtons(kase.precert_buttons, "precert", "Precert");
  mapButtons(kase.review_buttons, "review", "Review PA");
  mapButtons(kase.update_buttons, "update", "Update PA");
  mapButtons(kase.rx_pa_buttons, "rx_pa", "RX PA");
  mapButtons(kase.update_pa_buttons, "update_pa", "Update RX PA");
  mapButtons(kase.review_pa_buttons, "review_pa", "Review RX PA");

  if(kase.no_action_required){
     labels.push("No Action Required");
  }
  return labels.join(", ");
};

export const exportToExcel = (
  jsonData: CaseListingType[],
  fileName: string
) => {
  // Step 1: Convert JSON to worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  const topRow = worksheet.addRow([
    "ID",
    "Product",
    "Submitted",
    "Patient",
    "Submitting Practice",
    "Date of Birth",
    "Provider",
    "Status",
    "SP Status",
    "SP Substatus",
    "Payer",
    "Copay Status",
    "Next Steps",
  ]);
  topRow.font = { bold: true, size: 14 };
  topRow.alignment = { horizontal: "center" };

  const parsedJSONData: ExcelSheetCaseProps[] = [];
  jsonData.forEach((kase: CaseListingType) => {
    parsedJSONData.push({
      ID: kase.id.toString(),
      Product: kase.drug_name,
      Submitted: kase.created_at,
      Patient: kase?.patient?.firstlastname,
      "Submitting Practice": kase.submitting_practice,
      "Date of Birth": kase?.patient?.dob,
      Provider:  kase?.doctor?.firstlastname,
      Status: kase.status_name,
      "SP Status": kase.sp_status,
      "SP Substatus": kase.sp_substatus,
      Payer: kase.primary_insurance,
      "Copay Status": kase.copay_status,
      "Next Steps": extractButtonLabels(kase),
    });
    worksheet.addRow(
      Object.values({
        ID: kase.id.toString(),
        Product: kase.drug_name,
        Submitted: kase.created_at,
        Patient: kase?.patient?.firstlastname,
        "Submitting Practice": kase.submitting_practice,
        "Date of Birth": kase?.patient?.dob,
        Provider: kase?.doctor?.firstlastname,
        Status: kase.status_name,
        "SP Status": kase.sp_status,
        "SP Substatus": kase.sp_substatus,
        Payer: kase.primary_insurance,
        "Copay Status": kase.copay_status,
        "Next Steps": extractButtonLabels(kase),
      })
    );
  });

  // Set column widths to fit content
  [
    "ID",
    "Product",
    "Submitted",
    "Patient",
    "Submitting Practice",
    "Date of Birth",
    "Provider",
    "Status",
    "SP Status",
    "SP Substatus",
    "Payer",
    "Copay Status",
    "Next Steps",
  ].forEach((header: string, i: number) => {
    const maxLength = Math.max(
      header.length,
      ...parsedJSONData.map(
        (item: ExcelSheetCaseProps) =>
          String(item[header as ExcelSheetHeader] || "").length
      )
    );
    worksheet.getColumn(i + 1).width = maxLength + 5; // +2 for padding
    worksheet.getColumn(i + 1).alignment = { horizontal: "center" };

  });

  workbook.xlsx.writeBuffer().then((buffer: any) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  });
};

export const exportToExcelEOBs = (
  jsonData: EOBsExcelFileType[],
  fileName: string
) => {
  // Step 1: Convert JSON to worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  const topRow = worksheet.addRow([
    "EOB ID",
    "Claim Date",
    "Product",
    "SOC Type",
    "Insurance Name",
    "Insurance Type",
    "Insurance MAC",
    "Specialty",
    "Practice State",
    "HCPCS Code",
    "$ Billed",
    "$ Allowed",
    "$ Paid",
    "Patient Responsibility",
    "Coverage",
    "Created Date",
  ]);
  topRow.font = { bold: true, size: 14 };
  topRow.alignment = { horizontal: "center" };

  const parsedJSONData: ExcelSheetEOBsProps[] = [];
  jsonData.forEach((kase: EOBsExcelFileType) => {
    parsedJSONData.push({
      "EOB ID": kase.id.toString(),
      "Claim Date":kase.claim_date ? format(parse(kase.claim_date.split("T")[0], "yyyy-MM-dd", new Date()), "MM/dd/yyyy"):'',
       Product: kase.product,
      "SOC Type": kase.soc,
      "Insurance Name": kase.insurance_name,
      "Insurance Type":kase.insurance_type ? formatInsuranceType(kase.insurance_type) :'',
      "Insurance MAC": kase.insurance_mac,
       Specialty: kase.specialty,
      "Practice State": kase.practice_state,
      "CPT Code": kase.cpt_code,
       Billed:Number(kase.billed),
       Allowed:Number(kase.allowed),
       Paid:Number(kase.paid),
       Copay:Number(kase.copay),
       Coverage: kase.coverage,
      "Created Date":kase.created_at ? format(new Date(kase.created_at), "MM/dd/yyyy"):'',
    });
    worksheet.addRow(
      Object.values({
        "EOB ID": kase.id.toString(),
        "Claim Date":kase.claim_date ? format(parse(kase.claim_date.split("T")[0], "yyyy-MM-dd", new Date()), "MM/dd/yyyy"):'',
        Product: kase.product,
        "SOC Type": kase.soc,
        "Insurance Name": kase.insurance_name,
        "Insurance Type":kase.insurance_type ? formatInsuranceType(kase.insurance_type) :'',
        "Insurance MAC": kase.insurance_mac,
        Specialty: kase.specialty,
        "Practice State": kase.practice_state,
        "CPT Code": kase.cpt_code,
        Billed:kase.billed ? Number(kase.billed) :'',
        Allowed:kase.allowed ? Number(kase.allowed):'',
        Paid:kase.paid ? Number(kase.paid) :'',
        Copay:kase.copay ?  Number(kase.copay) :'',
        Coverage: kase.coverage,
        "Created Date":kase.created_at ? format(new Date(kase.created_at), "MM/dd/yyyy"):'',
      })
    );
  });

  // Set column widths to fit content
    const currencyColumns = ["Billed", "Allowed", "Paid", "Co-Ins/Copay"];

    [
        "EOB ID",
        "Claim Date",
        "Product",
        "SOC Type",
        "Insurance Name",
        "Insurance Type",
        "Insurance MAC",
        "Specialty",
        "Practice State",
        "HCPCS Code",
        "Billed",
        "Allowed",
        "Paid",
        "Patient Responsibility",
        "Coverage",
        "Created Date",
    ].forEach((header: string, i: number) => {
        const col = worksheet.getColumn(i + 1);

        const maxLength = Math.max(
            header.length,
            ...parsedJSONData.map(
                (item: ExcelSheetEOBsProps) =>
                    String(item[header as ExcelSheetEOBsHeader] || "").length
            )
        );
        col.width = maxLength + 5;
        //col.alignment = { horizontal: "center" };
        if (currencyColumns.includes(header)) {
            col.alignment = { horizontal: "right" };
            col.numFmt = '#,##0.00';
; // Excel currency format
        } else {
            col.alignment = { horizontal: "center" };
        }
    });


    workbook.xlsx.writeBuffer().then((buffer: any) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  });
};

export const formatInsuranceType = (str: string) =>{

  return str
    .replace(/_/g, ' ')                        // replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // capitalize each word
}
  